import type { MatrixGenerationJobPhase } from '@mss_creator/shared';

/**
 * French copy for the digest review surface (2026-08-26 design doc, D23).
 *
 * Same split as `context-files-field.tsx`: the backend answers with a
 * machine-readable `code` and keeps its own messages language-neutral, so
 * every string the reviewer reads lives here.
 *
 * Per-cause rather than generic, on purpose. The three realistic failures need
 * three different reactions — a missing Claude credential needs an
 * administrator, a 409 needs a reload, a model hiccup just needs a retry — and
 * one shared "l'opération a échoué" would send the user down the wrong path
 * two times out of three.
 */
const FAILURE_MESSAGES: Record<string, string> = {
  MATRIX_GENERATION_IN_PROGRESS:
    'Une génération est en cours pour cette matrice : elle lit ces digests pour composer son prompt, ils ne peuvent donc pas être modifiés maintenant.',
  CONFLICT:
    'Action impossible dans l’état actuel de la matrice — rechargez la page pour repartir de l’état à jour.',
  NO_ACTIVE_CLAUDE_CREDENTIAL:
    'Aucun accès Claude n’est actif : un administrateur doit en activer un avant qu’un digest puisse être généré. La saisie à la main reste possible.',
  CLAUDE_API_MODE_UNSUPPORTED:
    'L’accès Claude actif est une clé API, un mode que la génération ne prend pas en charge. Un administrateur doit activer un accès OAuth.',
  MATRIX_FRAMING_REQUIRED:
    'Renseignez le sujet et le contexte de la matrice avant de produire les digests : le digest extrait de chaque document ce qui est pertinent pour cette matrice.',
  NOT_FOUND: 'Ce document n’existe plus — rechargez la page.',
  VALIDATION_ERROR:
    'Le texte a été refusé : vérifiez qu’il n’est ni vide ni plus long que la limite indiquée.',
  INTERNAL_ERROR: 'L’appel au modèle a échoué. Réessayez dans un instant.',
};

/**
 * Read duck-typed rather than through `instanceof ApiError`, exactly as
 * `context-files-field.tsx` does: the `code` is the whole contract, and
 * component tests mock `lib/api` wholesale, which would leave a real
 * `instanceof` check permanently false.
 */
export function digestFailureMessage(error: unknown): string {
  const code = (error as { code?: string } | null)?.code;
  if (code && FAILURE_MESSAGES[code]) return FAILURE_MESSAGES[code];
  return 'L’opération a échoué. Réessayez dans un instant.';
}

/**
 * What a running job is doing right now (D14). A `digest_only` job never
 * reaches `cells`, but the label is here anyway because the panel reads the
 * same job row a full generation writes to.
 */
const PHASE_LABELS: Record<MatrixGenerationJobPhase, string> = {
  digest: 'Analyse des documents…',
  synthesis: 'Synthèse des documents…',
  cells: 'Génération de la matrice…',
};

/** `phase` is null while a job is still `pending`, and on jobs that predate phases. */
export function phaseLabel(phase: MatrixGenerationJobPhase | null): string {
  return phase ? PHASE_LABELS[phase] : 'Préparation…';
}
